import {axiosByMethod, getAxios} from '@api';
import {notifyError} from '@components/Notification';

/**
 * Lädt/speichert die individuellen Felder (Custom Fields) einer Entity.
 *
 * Bewusst KEIN afterDataLoad/afterDataLoadAddition — die Hooks sind von
 * importChecked- bzw. tagTrait-Mixin belegt und Vue überschreibt Mixin-Methoden
 * statt sie zu verketten. Stattdessen: visible-Watcher fürs Laden und $on auf
 * die eigenen 'created'/'updated'-Events (ItemForm.js) fürs Speichern.
 */
export default {
    data: () => ({
        customFieldValues: {},
        customFieldErrors: {},
    }),

    computed: {
        customFieldDefinitions() {
            return (this.options && this.options.customFieldDefinitions) || [];
        },

        customFieldEntityType() {
            return this.customFieldDefinitions.length ? this.customFieldDefinitions[0].entityType : null;
        },
    },

    watch: {
        visible(isOpen) {
            if (isOpen) {
                this.initCustomFieldValues();
            }
        },
    },

    created() {
        // ItemForm.js emittet nach erfolgreichem Save auf sich selbst —
        // aber nur bei nicht-leerem Response-Body (alle aktuellen Endpoints liefern die Entity zurück)
        this.$on('created', data => this.persistCustomFieldValues(data && data.id));
        this.$on('updated', () => this.persistCustomFieldValues(this.form.id));
    },

    methods: {
        initCustomFieldValues() {
            this.customFieldErrors = {};
            if (!this.customFieldEntityType) {
                this.customFieldValues = {};
                return;
            }

            if (this.form.id) {
                getAxios(`custom_field_values/${this.customFieldEntityType}/${this.form.id}`)
                    .then(response => {
                        this.customFieldValues = response.data || {};
                    }, () => {
                        this.customFieldValues = {};
                    });
            } else {
                const defaults = {};
                this.customFieldDefinitions.forEach(definition => {
                    if (definition.defaultValue === null || definition.defaultValue === '') {
                        return;
                    }
                    if (definition.type === 'boolean') {
                        defaults[definition.fieldKey] = definition.defaultValue === '1';
                    } else if (definition.type === 'multiselect') {
                        defaults[definition.fieldKey] = [definition.defaultValue];
                    } else {
                        defaults[definition.fieldKey] = definition.defaultValue;
                    }
                });
                this.customFieldValues = defaults;
            }
        },

        // Von ItemForm.js vor dem Save gerufen; false blockt den Save.
        // Pflicht muss clientseitig geprüft werden, weil die Werte erst NACH dem
        // Entity-Save persistiert werden (Modal wäre sonst schon zu).
        beforeSave() {
            const errors = {};
            this.customFieldDefinitions.filter(definition => definition.required).forEach(definition => {
                const value = this.customFieldValues[definition.fieldKey];
                const empty = value === undefined || value === null || value === ''
                    || value === false || (Array.isArray(value) && value.length === 0);
                if (empty) {
                    errors[definition.fieldKey] = 'Pflichtfeld';
                }
            });
            this.customFieldErrors = errors;
            if (Object.keys(errors).length > 0) {
                notifyError('Bitte alle Pflichtfelder bei den individuellen Feldern ausfüllen.');
                return false;
            }
            return true;
        },

        persistCustomFieldValues(id) {
            if (!id || !this.customFieldEntityType || this.customFieldDefinitions.length === 0) {
                return Promise.resolve();
            }
            return axiosByMethod('put',
                `custom_field_values/${this.customFieldEntityType}/${id}`, this.customFieldValues)
                .catch(error => {
                    const violations = (error.response && error.response.data && error.response.data.violations) || [];
                    notifyError('Individuelle Felder konnten nicht gespeichert werden: '
                        + (violations.map(v => `${v.propertyPath}: ${v.message}`).join(', ') || 'Serverfehler'));
                });
        },
    },
};
